/**
 * NIGHTBOUND - item catalogue.
 *
 * Item definitions are data. The starting loadout is configuration, not
 * something scattered across components, and every effect is applied by the
 * server when an action is resolved.
 */

export const ITEM_CATEGORIES = ['FOOD', 'CARE', 'TOY', 'GEAR', 'MATERIAL'] as const
export type ItemCategory = (typeof ITEM_CATEGORIES)[number]

export const ITEM_RARITIES = ['COMMON', 'UNCOMMON', 'RARE', 'CORRUPT'] as const
export type ItemRarity = (typeof ITEM_RARITIES)[number]

export interface ItemEffects {
  hunger?: number
  hygiene?: number
  bond?: number
  sanity?: number
  fitness?: number
  hp?: number
}

export interface ItemDefinition {
  key: string
  name: string
  description: string
  /** Extra atmosphere text shown in the inventory detail view. */
  flavour: string
  category: ItemCategory
  rarity: ItemRarity
  /** Consumables are removed from the inventory when used. */
  consumable: boolean
  maxStack: number
  /** Single glyph used when no artwork exists. */
  glyph: string
  /** Immediate effects applied on use. */
  effects?: ItemEffects
  /**
   * Bonus granted while the player *owns* the item (tools). Multiplier form so
   * effects stay declarative and server-applied.
   */
  toolBonus?: {
    appliesTo: 'CLEAN' | 'PLAY' | 'TRAIN'
    hygieneMultiplier?: number
    bondMultiplier?: number
    fitnessMultiplier?: number
    trainingXpMultiplier?: number
    note: string
  }
  /** Future economy hook - unused until the shop/Adventure exists. */
  value?: number
}

export const ITEMS: Record<string, ItemDefinition> = {
  basic_meat: {
    key: 'basic_meat',
    name: 'Basic Meat',
    description: 'A dense slab of unidentifiable muscle. Filling, and honest about what it is.',
    flavour: 'It was something, once. The creature does not care what.',
    category: 'FOOD',
    rarity: 'COMMON',
    consumable: true,
    maxStack: 99,
    glyph: '🥩',
    effects: { hunger: 32, bond: 1.5, sanity: -0.5, hp: 2 },
    value: 6,
  },
  dry_food: {
    key: 'dry_food',
    name: 'Dry Food',
    description: 'Hard brown pellets from a bag with no label. Keeps indefinitely.',
    flavour: 'Tastes of dust. The creature eats it anyway.',
    category: 'FOOD',
    rarity: 'COMMON',
    consumable: true,
    maxStack: 99,
    glyph: '🥣',
    effects: { hunger: 22, bond: 1, sanity: 0.2 },
    value: 3,
  },
  brush: {
    key: 'brush',
    name: 'Brush',
    description: 'Bristles worn down to stubble. It still works, and the creature remembers it.',
    flavour: 'Someone loved something with this before you did.',
    category: 'CARE',
    rarity: 'COMMON',
    consumable: false,
    maxStack: 1,
    glyph: '🪥',
    toolBonus: {
      appliesTo: 'CLEAN',
      hygieneMultiplier: 1.25,
      bondMultiplier: 1.2,
      note: 'Grooming restores more hygiene and builds more trust.',
    },
    value: 14,
  },
  cleaning_cloth: {
    key: 'cleaning_cloth',
    name: 'Cleaning Cloth',
    description: 'Grey rag, stiff with old grime. Consumed when you scrub the shelter floor.',
    flavour: 'There is never a clean one.',
    category: 'CARE',
    rarity: 'COMMON',
    consumable: true,
    maxStack: 99,
    glyph: '🧽',
    effects: { hygiene: 12, sanity: 0.5 },
    toolBonus: {
      appliesTo: 'CLEAN',
      hygieneMultiplier: 1.1,
      note: 'A fresh cloth lifts more dirt.',
    },
    value: 4,
  },
  play_ball: {
    key: 'play_ball',
    name: 'Play Ball',
    description: 'Cracked rubber sphere. Something has chewed it before.',
    flavour: 'It still bounces. Mostly.',
    category: 'TOY',
    rarity: 'COMMON',
    consumable: false,
    maxStack: 1,
    glyph: '🎾',
    toolBonus: {
      appliesTo: 'PLAY',
      fitnessMultiplier: 1.25,
      bondMultiplier: 1.2,
      note: 'Play is more energetic and more bonding.',
    },
    value: 12,
  },
  training_gear: {
    key: 'training_gear',
    name: 'Training Gear',
    description: 'Straps, weights and a post wrapped in rope. Everything needed to make a creature harder to kill.',
    flavour: 'The rope has been replaced many times.',
    category: 'GEAR',
    rarity: 'UNCOMMON',
    consumable: false,
    maxStack: 1,
    glyph: '🪢',
    toolBonus: {
      appliesTo: 'TRAIN',
      trainingXpMultiplier: 1.15,
      note: 'Training sessions yield more progress.',
    },
    value: 25,
  },
}

export const ITEM_KEYS = Object.keys(ITEMS)

export function getItem(key: string): ItemDefinition | undefined {
  return ITEMS[key]
}

export function isItemKey(key: string): boolean {
  return Object.prototype.hasOwnProperty.call(ITEMS, key)
}

export interface StartingItem {
  itemKey: string
  quantity: number
}

/** New players are given exactly this. Seeded from configuration, not code. */
export const STARTING_INVENTORY: StartingItem[] = [
  { itemKey: 'basic_meat', quantity: 5 },
  { itemKey: 'dry_food', quantity: 5 },
  { itemKey: 'brush', quantity: 1 },
  { itemKey: 'cleaning_cloth', quantity: 3 },
  { itemKey: 'play_ball', quantity: 1 },
  { itemKey: 'training_gear', quantity: 1 },
]

/** Foods offered by the FEED action. */
export const FEEDABLE_ITEMS = ITEM_KEYS.filter((k) => ITEMS[k]!.category === 'FOOD')
